import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import {
  GetWorkflowTemplatesCERT,
  UpdateWorkflowTemplateCERT,
  SyncWorkflowTemplatesFromSourceCERT,
} from '../../../redux/actions/certification/workflowTemplatesActions';

import CreateTemplateModal from './CreateTemplateModal';


const TemplatesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.certification);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Explicit default sort persists across toggles so rows never jump. The
  // switches update updatedAt on the backend, but the default ORDER BY is
  // createdAt DESC (immutable), so the order stays fixed.
  const [sort, setSort] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [rowBusy, setRowBusy] = useState({});

  const [templatesList, setTemplatesList] = useState([]);
  const [templatesTotal, setTemplatesTotal] = useState(0);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: 'Name', sort: 'name' },
    { label: 'External Template ID', sort: null },
    { label: 'Version', sort: 'version' },
    { label: 'Globally Enabled', sort: null },
    { label: 'Globally Visible', sort: null },
    { label: 'Allotted Authorities', sort: null },
    { label: 'Time stamps', sort: 'createdAt' },
  ];

  const handleSort = (value) => {
    if (sort === value) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setSortDirection('asc');
    }
  };

  const handleGet = () => {
    let query = '';
    if (search) query += `&search=${search}`;
    if (sort) query += `&field=${sort}`;
    if (sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetWorkflowTemplatesCERT(page + 1, rowsPerPage, query, setLoading));
  };

  useEffect(() => {
    handleGet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setTemplatesList(state.workflowTemplatesList?.records || []);
      setTemplatesTotal(state.workflowTemplatesList?.total || 0);
    } catch (err) {}
  }, [state.workflowTemplatesList]);

  const setBusy = (id, field, val) =>
    setRowBusy((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: val } }));

  // Toggle a global flag WITHOUT re-fetching — mutate the row in place so
  // the row order never shifts under the user. Falls back to a full reload
  // only if the backend call fails (we snap the flag back).
  const toggleField = async (template, field) => {
    setBusy(template.id, field, true);
    const nextValue = !template[field];
    // Optimistic
    setTemplatesList((list) => list.map((t) => (
      t.id === template.id ? { ...t, [field]: nextValue } : t
    )));
    const { statusCode } = await dispatch(
      UpdateWorkflowTemplateCERT(template.id, { [field]: nextValue })
    );
    setBusy(template.id, field, false);
    if (statusCode !== 200) {
      // revert
      setTemplatesList((list) => list.map((t) => (
        t.id === template.id ? { ...t, [field]: !nextValue } : t
      )));
    }
  };

  const handleSyncFromSource = async () => {
    const { statusCode } = await dispatch(SyncWorkflowTemplatesFromSourceCERT(setSyncing));
    if (statusCode === 200) handleGet();
  };

  return (
    <Box>

      <CreateTemplateModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        handleGet={handleGet}
      />

      <TopBar
        title={"Workflow Templates"}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        button2Text={syncing ? "Syncing…" : "Sync from Workflow Service"}
        button2OnClick={handleSyncFromSource}
        button2Disabled={syncing}
        button1Text={"Create new template"}
        button1OnClick={() => setCreateModalOpen(true)}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          templatesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No templates found. Click "Create new template" to add one.</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sort === heading.sort ? sortDirection : 'asc'}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                templatesList.map((row) => {
                  const allottedCount = (row.authority_allotments || []).length;
                  const globallyOff = !row.is_globally_enabled || !row.is_globally_visible;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant='body2' fontWeight={600}>
                            {row.name || <NA />}
                          </Typography>
                          {
                            globallyOff
                            &&
                            <Chip
                              size="small"
                              variant="filled"
                              color="warning"
                              label="Globally OFF"
                            />
                          }
                        </Stack>
                        {
                          row.description
                          ?
                          <Typography variant='caption' color={"grey"}>
                            {row.description}
                          </Typography>
                          : null
                        }
                      </TableCell>
                      <TableCell>
                        <Typography variant='caption' color={"grey"}>
                          {row.external_template_id || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.version ?? <NA />}</TableCell>
                      <TableCell>
                        <Switch
                          checked={!!row.is_globally_enabled}
                          disabled={!!rowBusy[row.id]?.is_globally_enabled}
                          onChange={() => toggleField(row, 'is_globally_enabled')}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!row.is_globally_visible}
                          disabled={!!rowBusy[row.id]?.is_globally_visible}
                          onChange={() => toggleField(row, 'is_globally_visible')}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant={allottedCount > 0 ? "filled" : "outlined"}
                          color={allottedCount > 0 ? "primary" : "default"}
                          label={allottedCount}
                        />
                      </TableCell>
                      <TableCell>
                        <TableTimeStamps
                          createdAt={row.createdAt}
                          updatedAt={row.updatedAt}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={tableHeadingsList.length}
                  count={templatesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

    </Box>
  );
};

export default TemplatesPage;
